import { useCallback, useMemo } from "react";

import type {
    ColumnPickerPluginConfig,
    ColumnPluginContext,
    ColumnListPluginContext,
    PluginRenderResult,
    HeaderPluginSlot,
    ColumnPluginSlot,
    ColumnListPluginSlot,
} from "./types";

export type PluginRegistry = {
    getColumnPluginsForSlot: (slot: ColumnPluginSlot, context: ColumnPluginContext) => readonly PluginRenderResult[];

    getHeaderPluginsForSlot: (slot: HeaderPluginSlot) => readonly PluginRenderResult[];

    getListPluginsForSlot: (
        slot: ColumnListPluginSlot,
        context: ColumnListPluginContext,
    ) => readonly PluginRenderResult[];
};

/**
 * Shared so that the destructuring defaults below keep a stable identity. A fresh `[]` per render —
 * which is what happens whenever `config` is null or omits a slot — invalidates every `useCallback`
 * here, and with them every memoised row downstream.
 */
const EMPTY_PLUGINS = [] as const;

export const usePluginRegistry = (config: ColumnPickerPluginConfig | undefined | null): PluginRegistry => {
    const { columnPlugins = EMPTY_PLUGINS, headerPlugins = EMPTY_PLUGINS, listPlugins = EMPTY_PLUGINS } = config ?? {};

    const getColumnPluginsForSlot = useCallback(
        (slot: ColumnPluginSlot, context: ColumnPluginContext): readonly PluginRenderResult[] => {
            return columnPlugins
                .filter((plugin) => plugin.slot === slot && plugin.shouldRender(context))
                .map((plugin) => ({
                    ...plugin.render(context),
                    behavior: plugin.behavior,
                }))
                .sort((a, b) => (a.priority ?? 10) - (b.priority ?? 10));
        },
        [columnPlugins],
    );

    const getHeaderPluginsForSlot = useCallback(
        (slot: HeaderPluginSlot): readonly PluginRenderResult[] => {
            return headerPlugins
                .filter((plugin) => plugin.slot === slot)
                .map((plugin) => plugin.render())
                .sort((a, b) => (a.priority ?? 10) - (b.priority ?? 10));
        },
        [headerPlugins],
    );

    const getListPluginsForSlot = useCallback(
        (slot: ColumnListPluginSlot, context: ColumnListPluginContext): readonly PluginRenderResult[] => {
            return listPlugins
                .filter((plugin) => plugin.slot === slot && plugin.shouldRender())
                .map((plugin) => plugin.render(context))
                .sort((a, b) => (a.priority ?? 10) - (b.priority ?? 10));
        },
        [listPlugins],
    );

    return useMemo(
        () => ({ getColumnPluginsForSlot, getHeaderPluginsForSlot, getListPluginsForSlot }),
        [getColumnPluginsForSlot, getHeaderPluginsForSlot, getListPluginsForSlot],
    );
};
